import { useEffect, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { Loader2, Save, LogOut, Shield, UserCog, Mail, User as UserIcon, KeyRound } from 'lucide-react'
import { apiGet, apiPut } from '../lib/api'
import { useAuth } from '../store/auth'

type Rol = 'admin' | 'operator'

interface MeData {
  data: {
    id: string
    email: string
    nombre: string
    rol: Rol
    activo: boolean
    creadoEn: string | null
    actualizadoEn: string | null
  }
}

function rolBadge(rol: Rol) {
  if (rol === 'admin') {
    return (
      <Badge variant="danger">
        <Shield className="size-3" /> Administrador
      </Badge>
    )
  }
  return (
    <Badge variant="info">
      <UserCog className="size-3" /> Operador
    </Badge>
  )
}

export function MiCuenta() {
  const logout = useAuth((s) => s.logout)

  const { data, isLoading, refetch } = useQuery<MeData>({
    queryKey: ['admin-me'],
    queryFn: () => apiGet('/api/admin/me'),
  })

  // Sección datos personales
  const [nombre, setNombre] = useState('')
  const [email, setEmail] = useState('')
  const [dataMsg, setDataMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  useEffect(() => {
    if (data?.data) {
      setNombre(data.data.nombre)
      setEmail(data.data.email)
    }
  }, [data])

  const updateMeMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPut('/api/admin/me', body),
  })

  function onSaveDatos(e: React.FormEvent) {
    e.preventDefault()
    setDataMsg(null)
    const body: Record<string, unknown> = {}
    if (data?.data) {
      if (nombre.trim() !== data.data.nombre) body.name = nombre.trim()
      if (email.trim().toLowerCase() !== data.data.email) body.email = email.trim()
    }
    if (Object.keys(body).length === 0) {
      setDataMsg({ kind: 'ok', text: 'Sin cambios' })
      return
    }
    updateMeMut.mutate(body, {
      onSuccess: () => {
        setDataMsg({ kind: 'ok', text: 'Datos actualizados' })
        refetch()
      },
      onError: (e: Error) => setDataMsg({ kind: 'err', text: e.message }),
    })
  }

  // Sección password
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwMsg, setPwMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  const changePwMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPut('/api/admin/me', body),
  })

  function onChangePassword(e: React.FormEvent) {
    e.preventDefault()
    setPwMsg(null)
    if (!currentPassword) return setPwMsg({ kind: 'err', text: 'Ingresá tu contraseña actual' })
    if (newPassword.length < 6)
      return setPwMsg({ kind: 'err', text: 'La nueva contraseña debe tener al menos 6 caracteres' })
    if (newPassword !== confirmPassword)
      return setPwMsg({ kind: 'err', text: 'La confirmación no coincide' })

    changePwMut.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          setPwMsg({ kind: 'ok', text: 'Contraseña actualizada' })
          setCurrentPassword('')
          setNewPassword('')
          setConfirmPassword('')
        },
        onError: (e: Error) => setPwMsg({ kind: 'err', text: e.message }),
      },
    )
  }

  return (
    <>
      <Topbar title="Mi cuenta" subtitle="Gestiona tus datos personales y contraseña" />
      <div className="p-6">
        {isLoading && (
          <div className="grid place-items-center py-20 text-ink-500">
            <Loader2 className="size-8 animate-spin" />
            <p className="mt-3 text-sm">Cargando…</p>
          </div>
        )}

        {data?.data && (
          <div className="mx-auto max-w-2xl space-y-6">
            {/* Header card */}
            <div className="flex items-center gap-4 rounded-2xl border border-ink-200 bg-white p-5">
              <div className="grid size-14 place-items-center rounded-full bg-brand-100 text-xl font-bold text-brand-700">
                {(data.data.nombre || data.data.email).slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="text-lg font-semibold text-ink-900">
                  {data.data.nombre || '(sin nombre)'}
                </div>
                <div className="text-sm text-ink-500">{data.data.email}</div>
              </div>
              <div>{rolBadge(data.data.rol)}</div>
            </div>

            {/* Datos personales */}
            <section className="rounded-2xl border border-ink-200 bg-white">
              <div className="border-b border-ink-100 px-5 py-3">
                <h2 className="text-sm font-semibold text-ink-900">Datos personales</h2>
                <p className="text-xs text-ink-500">Tu nombre y email para acceder al panel</p>
              </div>
              <form onSubmit={onSaveDatos} className="space-y-4 px-5 py-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-ink-700">Nombre</label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
                    <input
                      type="text"
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-ink-700">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
                    />
                  </div>
                </div>

                {dataMsg && (
                  <div
                    className={`rounded-lg border px-3 py-2 text-sm ${
                      dataMsg.kind === 'ok'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-red-200 bg-red-50 text-red-700'
                    }`}
                  >
                    {dataMsg.text}
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={updateMeMut.isPending}
                    className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
                  >
                    {updateMeMut.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Save className="size-4" />
                    )}
                    Guardar
                  </button>
                </div>
              </form>
            </section>

            {/* Cambiar contraseña */}
            <section className="rounded-2xl border border-ink-200 bg-white">
              <div className="border-b border-ink-100 px-5 py-3">
                <h2 className="text-sm font-semibold text-ink-900">Cambiar contraseña</h2>
                <p className="text-xs text-ink-500">
                  Ingresá tu contraseña actual y luego la nueva
                </p>
              </div>
              <form onSubmit={onChangePassword} className="space-y-4 px-5 py-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-ink-700">
                    Contraseña actual
                  </label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-ink-700">
                    Nueva contraseña
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-ink-700">
                    Confirmar nueva contraseña
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </div>

                {pwMsg && (
                  <div
                    className={`rounded-lg border px-3 py-2 text-sm ${
                      pwMsg.kind === 'ok'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-red-200 bg-red-50 text-red-700'
                    }`}
                  >
                    {pwMsg.text}
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={changePwMut.isPending}
                    className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
                  >
                    {changePwMut.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <KeyRound className="size-4" />
                    )}
                    Cambiar contraseña
                  </button>
                </div>
              </form>
            </section>

            {/* Cerrar sesión */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => logout()}
                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
              >
                <LogOut className="size-4" />
                Cerrar sesión
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default MiCuenta
